import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { Box, Text, Button, Modal } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { triggerMidasFlow } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { isValidEmail } from '@rsos/utils/validators';
import MultiEntryInput from '../../base/form/inputs/MultiEntryInput/MultiEntryInput';
import StyledSmartLink from './StyledSmartLink';
import { trackAlertShareSubmit } from './alertShareIncidentTrackings';
import {
  validationSchema,
  phoneNumberValidator,
  CALL_FLOW,
  initialValues,
  SHARE_SUCCESS_MESSAGE,
  SHARE_ERROR_MESSAGE,
  getSanitizedUniqueValues,
} from './helpers';

const ShareIncidentModal = ({ isOpened, onClose, alert }) => {
  const dispatch = useDispatch();
  const [emailTags, setEmailTags] = useState([]);
  const [isShareInProgress, setIsShareInProgress] = useState(false);
  const [phoneNumberTags, setPhoneNumberTags] = useState([]);

  const handleClose = resetForm => {
    resetForm();
    setEmailTags([]);
    setPhoneNumberTags([]);
    onClose();
  };
  const { currentOrg } = useSelector(state => state.sinatra.user);
  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const handleShareIncident = (values, resetForm) => {
    setIsShareInProgress(true);
    const variables = {
      phone_numbers: getSanitizedUniqueValues(values, 'phoneNumbersCumulative'),
      emails: getSanitizedUniqueValues(values, 'emailsCumulative'),
      alert_id: alert?.alert_id,
      organization: currentOrg.name,
    };
    trackAlertShareSubmit(
      values.emailsCumulative.length,
      values.phoneNumbersCumulative.length,
      irpVersion,
    );
    dispatch(
      triggerMidasFlow({
        callflow: CALL_FLOW,
        variables,
      }),
    )
      .then(action => {
        if (action.type === 'alerts/triggerMidasFlow/rejected') {
          return notifyError(SHARE_ERROR_MESSAGE);
        } else {
          notifySuccess(SHARE_SUCCESS_MESSAGE);
        }
      })
      .finally(() => {
        setIsShareInProgress(false);
        handleClose(resetForm);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) =>
        handleShareIncident(values, resetForm)
      }
    >
      {({
        values,
        resetForm,
        handleChange,
        setFieldValue,
        errors,
        isValid,
      }) => (
        <Modal
          isOpened={isOpened}
          onClose={() => handleClose(resetForm)}
          width={400}
          id="modal-share-incident"
        >
          <>
            <ModalHeader onClose={() => handleClose(resetForm)}>
              <Box width={400} flow={8} color="primaryText">
                <Text size="xlarge">Share Event Data</Text>
                <Text size="small">
                  A link to the event data will be sent to all recipients.
                </Text>
              </Box>
            </ModalHeader>
            <ModalBody mt={19}>
              <Form id="shareIncidentForm">
                <Box flow={16}>
                  <MultiEntryInput
                    value={values.email}
                    label="Email recipients"
                    name="email"
                    placeholder="Enter or paste emails"
                    hiddenValue={values.emailsCumulative}
                    hiddenInputName="emailsCumulative"
                    tags={emailTags}
                    updateTags={setEmailTags}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    validator={isValidEmail}
                    error={errors.emailsCumulative}
                  />
                  <MultiEntryInput
                    inputType="tel"
                    label="SMS recipients"
                    value={values.phoneNumber}
                    name="phoneNumber"
                    placeholder="Enter or paste phone numbers"
                    hiddenValue={values.phoneNumbersCumulative}
                    hiddenInputName="phoneNumbersCumulative"
                    tags={phoneNumberTags}
                    updateTags={setPhoneNumberTags}
                    setFieldValue={setFieldValue}
                    validator={phoneNumberValidator}
                    error={errors.phoneNumbersCumulative}
                  />
                  <Box horizontal align="center" flow={12}>
                    <Field type="checkbox" name="consentShare" />
                    <Text size="small" color="primaryText">
                      Recipients have verbally consented with this{' '}
                      <StyledSmartLink to="https://rapidsos.com/legal/sms-script/">
                        script
                      </StyledSmartLink>{' '}
                      to receive a share link through email/SMS.
                    </Text>
                  </Box>
                </Box>
              </Form>
            </ModalBody>
            <ModalFooter mt={12}>
              <Button
                onClick={() => {}}
                grow
                noMargin
                type="submit"
                form="shareIncidentForm"
                isLoadingProp={isShareInProgress}
                disabled={
                  (values.emailsCumulative.length === 0 &&
                    values.phoneNumbersCumulative.length === 0) ||
                  !isValid
                }
              >
                Send
              </Button>
            </ModalFooter>
          </>
        </Modal>
      )}
    </Formik>
  );
};

export default ShareIncidentModal;
